/**
 * Floorplan Controls Component
 * 
 * Controls for the floorplan section in the navigation sidebar
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  useTheme as useMuiTheme,
  alpha,
  IconButton,
  InputAdornment,
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'theme/ThemeContext';
import { useToast } from 'contexts/ToastContext';
import Input from '../atoms/Input';
import InputLabel from '../atoms/InputLabel';
import ReducersIndex from 'reduxModules/ducks';
import { distanceToString } from 'utilities/format';
import type { RootState } from 'types/redux/state';

interface FloorplanControlsProps {
  onDimensionChange?: (dimension: string, value: number) => void;
  onCeilingHeightChange?: (value: number) => void;
}

const FloorplanControls: React.FC<FloorplanControlsProps> = ({
  onDimensionChange,
  onCeilingHeightChange,
}) => {
  const muiTheme = useMuiTheme();
  const { mode } = useTheme();
  const dispatch = useDispatch();
  const { showError } = useToast();
    
  // Get dimensions from Redux state (stored in meters)
  const dimensions = useSelector((state: RootState) => {
    const activeTab = state.container.activeTab;
    const currentArea = state.container.areas[activeTab];
    return currentArea ? currentArea.dimensions : state.container.dimensions;
  });
  
  // Get ceiling height from Redux state (stored in meters)
  const ceilingHeightFromRedux = useSelector((state: RootState) => {
    const activeTab = state.container.activeTab;
    const currentArea = state.container.areas[activeTab];
    // ceiling height is stored as min/max range, we use min value
    return currentArea?.ceilingHeight?.min || 2.9; // default to 2.9m if not set
  });
  
  // Get units from Redux state
  const isMeter = useSelector((state: any) => state.ui.units === 'meters');
  
  // Define min/max constraints for room dimensions
  const MIN_DIMENSION_METERS = 1.8288; // 6 feet in meters
  const MAX_DIMENSION_METERS = 30.48; // 100 feet in meters
  const MIN_DIMENSION_FEET = 6; // 6 feet minimum
  const MAX_DIMENSION_FEET = 100; // 100 feet maximum
  
  // Arithmetic converters — used for parsing/validation, not display formatting
  const metersToFeet = (meters: number) => meters / 0.3048;
  const feetToMeters = (feet: number) => feet * 0.3048;
  
  const [widthStr, setWidthStr] = useState(() => distanceToString(dimensions.width, isMeter));
  const [lengthStr, setLengthStr] = useState(() => distanceToString(dimensions.length, isMeter));

  // Store ceiling height in meters internally - initialize from Redux
  const [ceilingHeightMeters, setCeilingHeightMeters] = useState(ceilingHeightFromRedux);
  const [ceilingHeightStr, setCeilingHeightStr] = useState(() => distanceToString(ceilingHeightFromRedux, isMeter));
  
  // Track whether inputs have been manually edited (to avoid validation on unit switch)
  const [widthEdited, setWidthEdited] = useState(false);
  const [lengthEdited, setLengthEdited] = useState(false);
  const [ceilingEdited, setCeilingEdited] = useState(false);

  // Update display dimensions when Redux dimensions change (e.g., from reset)
  useEffect(() => {
    setWidthStr(distanceToString(dimensions.width, isMeter));
    setLengthStr(distanceToString(dimensions.length, isMeter));
    setWidthEdited(false);
    setLengthEdited(false);
  }, [dimensions.width, dimensions.length, isMeter]);
  
  // Update ceiling height when Redux value changes (e.g., when navigating back)
  useEffect(() => {
    setCeilingHeightMeters(ceilingHeightFromRedux);
    setCeilingHeightStr(distanceToString(ceilingHeightFromRedux, isMeter));
    // REMOVED: dispatch(ReducersIndex.container.setMicrophones({ ceilingHeight }))
    // This was overriding user's manual microphone height settings
    // Microphone height should only be updated via explicit user input in microphones panel
  }, [ceilingHeightFromRedux]);
  
  // Only update display values when units actually change
  useEffect(() => {
    const currentlyShowingMetric = widthStr.includes('m');
    if (isMeter !== currentlyShowingMetric) {
      setWidthStr(distanceToString(dimensions.width, isMeter));
      setLengthStr(distanceToString(dimensions.length, isMeter));
      setCeilingHeightStr(distanceToString(ceilingHeightMeters, isMeter));
      setWidthEdited(false);
      setLengthEdited(false);
      setCeilingEdited(false);
    }
  }, [isMeter]); // Only depend on isMeter changing

  const handleDimensionChange = (dimension: 'width' | 'length', value: number) => {
    const revert = () => {
      if (dimension === 'width') setWidthStr(distanceToString(dimensions.width, isMeter));
      else setLengthStr(distanceToString(dimensions.length, isMeter));
    };

    if (!value || isNaN(value)) {
      showError('Please enter a valid number for dimensions');
      revert();
      return;
    }

    if (value <= 0) {
      showError('Dimensions must be greater than zero');
      revert();
      return;
    }

    const valueInMeters = isMeter ?
      Math.round(value * 100) / 100 :
      Math.round(feetToMeters(value) * 100) / 100;

    if (valueInMeters < MIN_DIMENSION_METERS - 0.001) {
      showError(`Minimum ${dimension} is ${distanceToString(MIN_DIMENSION_METERS, isMeter)}`);
      revert();
      return;
    }

    if (valueInMeters > MAX_DIMENSION_METERS + 0.001) {
      showError(`Maximum ${dimension} is ${distanceToString(MAX_DIMENSION_METERS, isMeter)}`);
      revert();
      return;
    }
    
    // Only update state and dispatch if validation passes
    // Don't change the display value here - keep what the user typed
    // The validation passed, so just dispatch the Redux action
    if (dimension === 'width') {
      // Dispatch Redux action to update dimensions (always in meters)
      dispatch(ReducersIndex.container.setDimensions({
        width: valueInMeters,
        length: dimensions.length
      }));
      onDimensionChange?.('width', value);
    } else {
      // Dispatch Redux action to update dimensions (always in meters)
      dispatch(ReducersIndex.container.setDimensions({
        width: dimensions.width,
        length: valueInMeters
      }));
      onDimensionChange?.('length', value);
    }
  };

  const handleCeilingHeightChange = (value: number, originalInput?: string) => {
    // Validate input
    if (!value || isNaN(value)) {
      if (originalInput && originalInput.trim() !== '') {
        showError(`Invalid ceiling height format. Use formats like "10'" or "9'6"" for feet and inches`);
      } else {
        showError('Please enter a valid ceiling height');
      }
      setCeilingHeightStr(distanceToString(ceilingHeightMeters, isMeter));
      return;
    }

    if (value <= 0) {
      showError('Ceiling height must be greater than zero');
      setCeilingHeightStr(distanceToString(ceilingHeightMeters, isMeter));
      return;
    }

    // Value comes in current units, store in meters
    const valueInMeters = isMeter ? value : feetToMeters(value);

    // Validate ceiling height (min 8ft / 2.4384m, max 15ft / 4.572m)
    const MIN_CEILING_METERS = 2.4384; // 8 feet in meters
    const MAX_CEILING_METERS = 4.572; // 15 feet in meters

    if (valueInMeters < MIN_CEILING_METERS - 0.001) {
      // Round the displayed min UP to 1 decimal so the value shown is actually reachable
      // (distanceToString rounds to nearest, which can produce "2.4 m" for a 2.4384 m min — off-by-rounding bug).
      const minDisplay = isMeter
        ? `${(Math.ceil(MIN_CEILING_METERS * 10) / 10).toFixed(1)} m`
        : `${Math.round(MIN_CEILING_METERS / 0.3048)}'`;
      showError(`Minimum ceiling height is ${minDisplay}`);
      setCeilingHeightStr(distanceToString(ceilingHeightMeters, isMeter));
      return;
    }

    if (valueInMeters > MAX_CEILING_METERS + 0.001) {
      // Round the displayed max DOWN to 1 decimal so the value shown is actually reachable
      // (distanceToString rounds 4.572 → "4.6 m", but 4.6 m exceeds the actual 4.572 m / 15 ft limit).
      const maxDisplay = isMeter
        ? `${(Math.floor(MAX_CEILING_METERS * 10) / 10).toFixed(1)} m`
        : `${Math.round(MAX_CEILING_METERS / 0.3048)}'`;
      showError(`Maximum ceiling height is ${maxDisplay}`);
      setCeilingHeightStr(distanceToString(ceilingHeightMeters, isMeter));
      return;
    }
    
    // Only update if validation passes - keep user's input format
    setCeilingHeightMeters(valueInMeters);
    
    // Dispatch Redux action to update ceiling height (stored as min/max range)
    dispatch(ReducersIndex.container.setCeilingHeight({
      min: valueInMeters,
      max: valueInMeters
    }));

    // Force microphone height recalculation by passing the new ceiling height
    // This ensures bar height adjusts when ceiling is lowered
    dispatch(ReducersIndex.container.setMicrophones({ ceilingHeight: valueInMeters } as any));

    // Same follow-the-ceiling recalculation for the expansion ceiling mic (dcm_x) — a no-op
    // in setExpansionMicrophones when expansion isn't active or isn't the 'ceiling' type.
    dispatch(ReducersIndex.container.setExpansionMicrophones({ ceilingHeight: valueInMeters } as any));

    onCeilingHeightChange?.(valueInMeters);
  };

  // Arrow increment/decrement helper functions
  const handleLengthIncrement = () => {
    const currentValue = isMeter ? dimensions.length : metersToFeet(dimensions.length);
    const newValue = currentValue + (isMeter ? 0.1 : 0.25);
    setLengthStr(distanceToString(isMeter ? newValue : newValue * 0.3048, isMeter));
    handleDimensionChange('length', newValue);
  };

  const handleLengthDecrement = () => {
    const currentValue = isMeter ? dimensions.length : metersToFeet(dimensions.length);
    const newValue = Math.max(currentValue - (isMeter ? 0.1 : 0.25), isMeter ? MIN_DIMENSION_METERS : MIN_DIMENSION_FEET);
    setLengthStr(distanceToString(isMeter ? newValue : newValue * 0.3048, isMeter));
    handleDimensionChange('length', newValue);
  };

  const handleWidthIncrement = () => {
    const currentValue = isMeter ? dimensions.width : metersToFeet(dimensions.width);
    const newValue = currentValue + (isMeter ? 0.1 : 0.25);
    setWidthStr(distanceToString(isMeter ? newValue : newValue * 0.3048, isMeter));
    handleDimensionChange('width', newValue);
  };

  const handleWidthDecrement = () => {
    const currentValue = isMeter ? dimensions.width : metersToFeet(dimensions.width);
    const newValue = Math.max(currentValue - (isMeter ? 0.1 : 0.25), isMeter ? MIN_DIMENSION_METERS : MIN_DIMENSION_FEET);
    setWidthStr(distanceToString(isMeter ? newValue : newValue * 0.3048, isMeter));
    handleDimensionChange('width', newValue);
  };

  const handleCeilingHeightIncrement = () => {
    const currentValue = isMeter ? ceilingHeightMeters : metersToFeet(ceilingHeightMeters);
    const newValue = currentValue + (isMeter ? 0.1 : 0.25);
    setCeilingHeightStr(distanceToString(isMeter ? newValue : newValue * 0.3048, isMeter));
    handleCeilingHeightChange(newValue);
  };

  const handleCeilingHeightDecrement = () => {
    const currentValue = isMeter ? ceilingHeightMeters : metersToFeet(ceilingHeightMeters);
    const newValue = Math.max(currentValue - (isMeter ? 0.1 : 0.25), isMeter ? 2.0 : 6.5);
    setCeilingHeightStr(distanceToString(isMeter ? newValue : newValue * 0.3048, isMeter));
    handleCeilingHeightChange(newValue);
  };

  return (
    <Box sx={{ px: 0, py: 0 }}>
      <Stack spacing={4}>
        {/* Room Dimensions */}
        <Box>
          <InputLabel>Room dimensions</InputLabel>
          <Box sx={{ display: 'flex', gap: "5px", mt: 0.5 }}>
            <Input
              value={lengthStr}
              onChange={(e) => {
                // Allow typing any characters - don't strip units during editing
                setLengthStr(e.target.value);
                setLengthEdited(true); // Mark as edited by user
              }}
              onBlur={(e) => {
                // Only validate if user actually edited the value
                if (lengthEdited) {
                  const value = e.target.value;
                  let numValue: number;
                  
                  if (isMeter) {
                    // For metric, just parse the number (strip 'm' if present)
                    numValue = parseFloat(value.replace(/m/g, ''));
                  } else {
                    // For imperial, handle feet/inches format like "12'8"" or just "12'" or "12"
                    if (value.includes("'")) {
                      const parts = value.split("'");
                      const feet = parseFloat(parts[0]) || 0;
                      let inches = 0;
                      
                      if (parts[1] && parts[1].trim().length > 0) {
                        // Extract inches from the second part (remove quotes and any extra chars, parse)
                        const inchesStr = parts[1].replace(/[^\d.]/g, ''); // Keep only digits and decimal
                        inches = parseFloat(inchesStr) || 0;
                        // Ensure inches don't exceed 11.99 (should be less than 12)
                        inches = Math.min(inches, 11.99);
                      }
                      
                      numValue = feet + (inches / 12); // Convert to decimal feet
                    } else {
                      // Just a number without units
                      numValue = parseFloat(value.replace(/[^\d.]/g, '')) || 0;
                    }
                  }
                  
                  if (!isNaN(numValue) && numValue > 0) {
                    handleDimensionChange('length', numValue);
                    // Keep the current input value as-is, don't change it
                    setLengthEdited(false);
                  } else {
                    setLengthStr(distanceToString(dimensions.length, isMeter));
                    setLengthEdited(false); // Reset after handling
                  }
                }
              }}
              inputProps={{
                inputMode: 'text', // Allow text input for feet/inches format
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      marginRight: '2px',
                      height: '28px',
                      justifyContent: 'space-between',
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      '.MuiInputBase-root:hover &': {
                        opacity: 1,
                      },
                    }}>
                      <IconButton
                        size="small"
                        onClick={handleLengthIncrement}
                        tabIndex={-1}
                        sx={{ 
                          padding: '0px',
                          minWidth: '16px',
                          width: '16px',
                          height: '14px',
                          borderRadius: '2px',
                          '& svg': {
                            fontSize: '18px',
                            color: 'text.secondary',
                          },
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                      >
                        <ArrowDropUpIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleLengthDecrement}
                        tabIndex={-1}
                        sx={{ 
                          padding: '0px',
                          minWidth: '16px',
                          width: '16px',
                          height: '14px',
                          borderRadius: '2px',
                          '& svg': {
                            fontSize: '18px',
                            color: 'text.secondary',
                          },
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  paddingRight: '4px',
                },
              }}
            />
            <Input
              value={widthStr}
              onChange={(e) => {
                // Allow typing any characters - don't strip units during editing
                setWidthStr(e.target.value);
                setWidthEdited(true); // Mark as edited by user
              }}
              onBlur={(e) => {
                // Only validate if user actually edited the value
                if (widthEdited) {
                  const value = e.target.value;
                  let numValue: number;
                  
                  if (isMeter) {
                    // For metric, just parse the number (strip 'm' if present)
                    numValue = parseFloat(value.replace(/m/g, ''));
                  } else {
                    // For imperial, handle feet/inches format like "12'8"" or just "12'" or "12"
                    if (value.includes("'")) {
                      const parts = value.split("'");
                      const feet = parseFloat(parts[0]) || 0;
                      let inches = 0;
                      
                      if (parts[1] && parts[1].trim().length > 0) {
                        // Extract inches from the second part (remove quotes and any extra chars, parse)
                        const inchesStr = parts[1].replace(/[^\d.]/g, ''); // Keep only digits and decimal
                        inches = parseFloat(inchesStr) || 0;
                        // Ensure inches don't exceed 11.99 (should be less than 12)
                        inches = Math.min(inches, 11.99);
                      }
                      
                      numValue = feet + (inches / 12); // Convert to decimal feet
                    } else {
                      // Just a number without units
                      numValue = parseFloat(value.replace(/[^\d.]/g, '')) || 0;
                    }
                  }
                  
                  if (!isNaN(numValue) && numValue > 0) {
                    handleDimensionChange('width', numValue);
                    // Keep the current input value as-is, don't change it
                    setWidthEdited(false);
                  } else {
                    setWidthStr(distanceToString(dimensions.width, isMeter));
                    setWidthEdited(false); // Reset after handling
                  }
                }
              }}
              inputProps={{
                inputMode: 'text', // Allow text input for feet/inches format
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      marginRight: '2px',
                      height: '28px',
                      justifyContent: 'space-between',
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      '.MuiInputBase-root:hover &': {
                        opacity: 1,
                      },
                    }}>
                      <IconButton
                        size="small"
                        onClick={handleWidthIncrement}
                        tabIndex={-1}
                        sx={{ 
                          padding: '0px',
                          minWidth: '16px',
                          width: '16px',
                          height: '14px',
                          borderRadius: '2px',
                          '& svg': {
                            fontSize: '18px',
                            color: 'text.secondary',
                          },
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                      >
                        <ArrowDropUpIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleWidthDecrement}
                        tabIndex={-1}
                        sx={{ 
                          padding: '0px',
                          minWidth: '16px',
                          width: '16px',
                          height: '14px',
                          borderRadius: '2px',
                          '& svg': {
                            fontSize: '18px',
                            color: 'text.secondary',
                          },
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  paddingRight: '4px',
                },
              }}
            />
          </Box>
        </Box>

        {/* Spacer */}
        <Box sx={{ height: '10px' }} />

        {/* Ceiling Height */}
        <Box sx={{display: 'flex', flexDirection: 'column'}}>
          <InputLabel sx={{ fontStyle: 'SemiBold'}}>Ceiling height</InputLabel>
          <Input
            value={ceilingHeightStr}
            onChange={(e) => {
              // Allow typing any characters - don't strip units during editing
              setCeilingHeightStr(e.target.value);
              setCeilingEdited(true); // Mark as edited by user
            }}
            onBlur={(e) => {
              // Only validate if user actually edited the value
              if (ceilingEdited) {
                const value = e.target.value;
                let numValue: number;
                
                if (isMeter) {
                  // For metric, just parse the number (strip 'm' if present)
                  numValue = parseFloat(value.replace(/m/g, ''));
                } else {
                  // For imperial, handle feet/inches format like "9'6"" or just "9'" or "9"
                  if (value.includes("'")) {
                    const parts = value.split("'");
                    const feet = parseFloat(parts[0]) || 0;
                    let inches = 0;
                    
                    if (parts[1] && parts[1].trim().length > 0) {
                      // Extract inches from the second part (remove quotes and any extra chars, parse)
                      const inchesStr = parts[1].replace(/[^\d.]/g, ''); // Keep only digits and decimal
                      inches = parseFloat(inchesStr) || 0;
                      // Ensure inches don't exceed 11.99 (should be less than 12)
                      inches = Math.min(inches, 11.99);
                    }
                    
                    numValue = feet + (inches / 12); // Convert to decimal feet
                  } else {
                    // Just a number without units
                    numValue = parseFloat(value.replace(/[^\d.]/g, '')) || 0;
                  }
                }
                
                if (!isNaN(numValue) && numValue > 0) {
                  handleCeilingHeightChange(numValue, value);
                  // Keep the current input value as-is, don't change it
                  setCeilingEdited(false);
                } else {
                  // Pass original value for better error message
                  handleCeilingHeightChange(NaN, value);
                  setCeilingEdited(false);
                }
              }
            }}
            inputProps={{ 
              step: 0.5,
              inputMode: 'text' // Allow text input for feet/inches format
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    marginRight: '2px',
                    height: '28px',
                    justifyContent: 'space-between',
                  }}>
                    <IconButton
                      size="small"
                      onClick={handleCeilingHeightIncrement}
                      tabIndex={-1}
                      sx={{ 
                        padding: '0px',
                        minWidth: '16px',
                        width: '16px',
                        height: '14px',
                        borderRadius: '2px',
                        '& svg': {
                          fontSize: '18px',
                          color: 'text.secondary',
                        },
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      <ArrowDropUpIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCeilingHeightDecrement}
                      tabIndex={-1}
                      sx={{ 
                        padding: '0px',
                        minWidth: '16px',
                        width: '16px',
                        height: '14px',
                        borderRadius: '2px',
                        '& svg': {
                          fontSize: '18px',
                          color: 'text.secondary',
                        },
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      <ArrowDropDownIcon />
                    </IconButton>
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{
              mt: 0.5,
              width: '50%',
              '& .MuiOutlinedInput-root': {
                paddingRight: '4px',
              },
            }}
          />
        </Box>


      </Stack>
    </Box>
  );
};

export default FloorplanControls;